import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { DialogWrapper } from '../../shared/components/common/dialogWrapper';
import { ButtonWrapper } from '../../shared/components/formUtilities/useButton';
import Logo from '../../shared/components/common/logo';
import { SigningService } from '../../services/signingService';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const SESSION_KEY = (id) => `signing_token_${id}`;

const STATUS = {
    AUTH_EMAIL: 'auth_email',
    AUTH_OTP: 'auth_otp',
    LOADING: 'loading',
    ACTIVE: 'active',
    COMPLETED: 'completed',
    REJECTED: 'rejected',
    ERROR: 'error',
};

export const SignPage = () => {
    const { signerProcessId } = useParams();

    const [status, setStatus] = useState(STATUS.LOADING);
    const [info, setInfo] = useState(null);
    const [maskedEmail, setMaskedEmail] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState('');
    const [numPages, setNumPages] = useState(null);
    const [pdfWidth, setPdfWidth] = useState(900);
    const [signing, setSigning] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const serviceRef = useRef(null);

    useEffect(() => {
        const updateWidth = () => setPdfWidth(Math.min(window.innerWidth - 64, 900));
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    const loadSigningInfo = useCallback((token) => {
        serviceRef.current = new SigningService(token);
        setStatus(STATUS.LOADING);
        serviceRef.current
            .getSigningInfo(signerProcessId)
            .then((resp) => {
                const data = resp.data;
                setInfo(data);
                if (data.status === 'Created') {
                    setStatus(STATUS.ACTIVE);
                } else if (data.status === 'Completed') {
                    setStatus(STATUS.COMPLETED);
                } else {
                    setStatus(STATUS.REJECTED);
                }
            })
            .catch((err) => {
                if (err?.response?.status === 401) {
                    sessionStorage.removeItem(SESSION_KEY(signerProcessId));
                    setStatus(STATUS.AUTH_EMAIL);
                } else {
                    setStatus(STATUS.ERROR);
                }
            });
    }, [signerProcessId]);

    useEffect(() => {
        let cancelled = false;
        const stored = sessionStorage.getItem(SESSION_KEY(signerProcessId));
        if (stored) {
            loadSigningInfo(stored);
        } else {
            new SigningService(null).getEmailHint(signerProcessId)
                .then((resp) => { if (!cancelled) { setMaskedEmail(resp.data?.maskedEmail ?? ''); } })
                .catch(() => { if (!cancelled) { setMaskedEmail('your registered email'); } })
                .finally(() => { if (!cancelled) { setStatus(STATUS.AUTH_EMAIL); } });
        }
        return () => { cancelled = true; };
    }, [signerProcessId, loadSigningInfo]);

    const handleRequestOtp = async () => {
        setAuthError('');
        setAuthLoading(true);
        try {
            await new SigningService(null).requestOtp(signerProcessId);
            setStatus(STATUS.AUTH_OTP);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data?.errors?.[0];
            setAuthError(msg || 'Could not send code. Please try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otpCode.trim()) return;
        setAuthError('');
        setAuthLoading(true);
        try {
            const resp = await new SigningService(null).verifyOtp(signerProcessId, otpCode.trim());
            const token = resp.data?.token;
            if (token) {
                sessionStorage.setItem(SESSION_KEY(signerProcessId), token);
                loadSigningInfo(token);
            } else {
                setAuthError('Verification failed. Please try again.');
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data?.errors?.[0];
            setAuthError(msg || 'Invalid or expired code. Please try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
        setNumPages(n);
    }, []);

    const handleSign = async () => {
        setSigning(true);
        try {
            const resp = await serviceRef.current.initiateSign(signerProcessId);
            const url = resp.data?.pendingSignUrl;
            if (url) {
                window.location.href = url;
            } else {
                setSigning(false);
            }
        } catch {
            setSigning(false);
        }
    };

    const handleCancelConfirm = async () => {
        setCancelling(true);
        try {
            await serviceRef.current.cancelSigning(signerProcessId);
            setCancelDialogOpen(false);
            setStatus(STATUS.REJECTED);
        } catch {
            setCancelling(false);
        }
    };

    if (status === STATUS.AUTH_EMAIL) {
        return (
            <Box className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
                <Box className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col gap-5">
                    <Logo />
                    <Box>
                        <Typography variant="h6" className="!font-semibold">
                            Verify your identity
                        </Typography>
                        <Typography variant="body2" className="!text-gray-500 mt-1">
                            We'll send a verification code to{' '}
                            <strong>{maskedEmail}</strong>.
                        </Typography>
                    </Box>
                    {authError && (
                        <Typography variant="body2" className="!text-red-500">
                            {authError}
                        </Typography>
                    )}
                    <ButtonWrapper
                        label="Send verification code"
                        loading={authLoading}
                        onClick={handleRequestOtp}
                    />
                </Box>
            </Box>
        );
    }

    if (status === STATUS.AUTH_OTP) {
        return (
            <Box className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
                <Box className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col gap-5">
                    <Logo />
                    <Box>
                        <Typography variant="h6" className="!font-semibold">
                            Enter verification code
                        </Typography>
                        <Typography variant="body2" className="!text-gray-500 mt-1">
                            We sent a 6-digit code to <strong>{maskedEmail}</strong>. It expires in 10 minutes.
                        </Typography>
                    </Box>
                    <TextField
                        label="Verification code"
                        fullWidth
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleVerifyOtp()}
                        error={!!authError}
                        helperText={authError}
                        inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                        size="small"
                    />
                    <ButtonWrapper
                        label="Verify"
                        loading={authLoading}
                        onClick={handleVerifyOtp}
                        disabled={!otpCode.trim()}
                    />
                    <ButtonWrapper
                        variant="text"
                        label="Resend code"
                        onClick={() => { setStatus(STATUS.AUTH_EMAIL); setAuthError(''); setOtpCode(''); }}
                        classList="!text-gray-500"
                    />
                </Box>
            </Box>
        );
    }

    if (status === STATUS.LOADING) {
        return (
            <Box className="flex items-center justify-center min-h-[60vh]">
                <CircularProgress />
            </Box>
        );
    }

    if (status === STATUS.ERROR) {
        return (
            <Box className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-center px-6">
                <Typography variant="h5" className="!font-semibold">
                    Link not found
                </Typography>
                <Typography className="!text-gray-500">
                    This signing link is invalid or has expired. Please contact Funding Souq if you need assistance.
                </Typography>
            </Box>
        );
    }

    if (status === STATUS.COMPLETED) {
        return (
            <Box className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-center px-6">
                <Box className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mb-2">
                    <Typography variant="h4" className="!text-white">✓</Typography>
                </Box>
                <Typography variant="h5" className="!font-semibold">
                    Document signed
                </Typography>
                <Typography className="!text-gray-500 max-w-md">
                    You have already signed this document. You will receive a copy by email once all parties have completed signing.
                </Typography>
            </Box>
        );
    }

    if (status === STATUS.REJECTED) {
        return (
            <Box className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-center px-6">
                <Typography variant="h5" className="!font-semibold">
                    Signing cancelled
                </Typography>
                <Typography className="!text-gray-500 max-w-md">
                    This signing request has been cancelled. Please contact Funding Souq if you have any questions.
                </Typography>
            </Box>
        );
    }

    return (
        <Box className="flex flex-col min-h-screen bg-gray-50">
            <Box className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
                <Box className="max-w-[960px] mx-auto flex items-center justify-between gap-4">
                    <Box>
                        <Typography variant="subtitle1" className="!font-semibold leading-tight">
                            Finance Contract – Application #{info?.applicationId}
                        </Typography>
                        <Typography variant="body2" className="!text-gray-500">
                            {info?.businessName} · Please review the document below before signing
                        </Typography>
                    </Box>
                    <Box className="flex gap-3 shrink-0">
                        <ButtonWrapper
                            variant="outlined"
                            label="Cancel"
                            classList="!text-red-500 !border-red-300 hover:!bg-red-50"
                            onClick={() => setCancelDialogOpen(true)}
                        />
                        <ButtonWrapper
                            label="Sign with UAE Pass"
                            loading={signing}
                            onClick={handleSign}
                        />
                    </Box>
                </Box>
            </Box>

            <Box className="flex-1 flex justify-center py-8 px-4 overflow-auto">
                {info?.documentUrl ? (
                    <Box>
                        <Document
                            file={info.documentUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={
                                <Box className="flex justify-center py-16">
                                    <CircularProgress />
                                </Box>
                            }
                            error={
                                <Box className="flex justify-center py-16">
                                    <Typography className="!text-gray-400">
                                        Could not load document preview.
                                    </Typography>
                                </Box>
                            }
                        >
                            {numPages &&
                                Array.from({ length: numPages }, (_, i) => (
                                    <Box key={i + 1} className="mb-4 shadow-md">
                                        <Page
                                            pageNumber={i + 1}
                                            width={pdfWidth}
                                            renderTextLayer
                                            renderAnnotationLayer
                                        />
                                    </Box>
                                ))}
                        </Document>
                    </Box>
                ) : (
                    <Box className="flex items-center justify-center h-64">
                        <Typography className="!text-gray-400">Document not available</Typography>
                    </Box>
                )}
            </Box>

            <Box className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <Box className="max-w-[960px] mx-auto flex justify-end gap-3">
                    <ButtonWrapper
                        variant="outlined"
                        label="Cancel"
                        classList="!text-red-500 !border-red-300 hover:!bg-red-50"
                        onClick={() => setCancelDialogOpen(true)}
                    />
                    <ButtonWrapper
                        label="Sign with UAE Pass"
                        loading={signing}
                        onClick={handleSign}
                    />
                </Box>
            </Box>

            <DialogWrapper
                state={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                title="Cancel signing"
                maxWidth="sm"
                content={
                    <Box className="flex flex-col gap-3">
                        <Typography>
                            Are you ready to cancel this signing request?
                        </Typography>
                        <Typography className="!text-gray-500">
                            If you are not ready to sign right now, simply close this dialog — you can come back later using the same link from your email or SMS.
                        </Typography>
                        <Typography className="!text-red-500 !text-sm">
                            <strong>Cancel permanently</strong> will reject the entire signing process and cannot be undone.
                        </Typography>
                    </Box>
                }
                actions={
                    <>
                        <ButtonWrapper
                            variant="outlined"
                            label="Come back later"
                            onClick={() => setCancelDialogOpen(false)}
                        />
                        <ButtonWrapper
                            label="Cancel permanently"
                            loading={cancelling}
                            onClick={handleCancelConfirm}
                            classList="!bg-red-500 hover:!bg-red-600"
                        />
                    </>
                }
            />
        </Box>
    );
};
